import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';

import './payment-info.scss';
import PaymentModal from 'components/payment/payment-modal/payment-modal';
import { ReactComponent as CoinIcon } from 'assets/icons/coin_icon.svg';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar_icon.svg';
import { ReactComponent as Calendar2Icon } from 'assets/icons/calendar2_icon.svg';
import { ReactComponent as CardIcon } from 'assets/icons/card_icon.svg';

// Data
import paymentPlanData from 'components/payment/payment-plans.json';
import PopupFrame from 'layouts/PopupFrame';
import Button from 'common/button/button';
import Label from 'common/label/label';
import { StatusType, useSubscribeStatus } from 'hooks/useSubscribe';
import useLoadingStore from 'zustands/useLoadingStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';
import moment from 'moment';
import { addComma } from 'constants/utils';

const PaymentInfo: React.FC = () => {
  const navigate = useNavigate();

  const { setLoadingState } = useLoadingStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [payCardInfo, setPayCardInfo] = React.useState<string>('');
  const [paymentHistory, setPaymentHistory] = React.useState<StatusType[]>([]);
  const [paymentModal, setPaymentModal] = React.useState<boolean>(false);

  const fetchPayInfo = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('get', `/auth/subscriptionList`);
      console.log('/auth/subscriptionList_data', data);

      if (data?.resultData?.user?.length > 0) {
        setPayCardInfo(data.resultData.payment);
        setPaymentHistory(data.resultData.user);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/subscriptionList');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  const toggleModal = (): any => {
    setPaymentModal(!paymentModal);
  };

  const changePlan = () => {
    navigate(`/payment`);
  };

  React.useEffect(() => {
    fetchPayInfo();
  }, []);

  const mainItem = paymentHistory[paymentHistory.length - 1];
  let activeStatus = '비활성';
  if (mainItem?.rss_status === 'ACTIVE') activeStatus = '이용중';
  if (mainItem?.rss_status === 'PAUSE') activeStatus = '일시정지';
  if (mainItem?.rss_status === 'CANCEL') activeStatus = '취소';
  if (mainItem?.rss_status === 'CANCEL_WAIT') activeStatus = '구독취소대기중';

  return (
    <>
      <PopupFrame>
        <div className="payment-info">
          <div className="payment-info__head">결제관리</div>
          <div className="payment-info__top">
            <div className="payment-info__content">
              <div className="payment-info__labels">
                <div className="payment-info__labels__label">
                  <Label text={activeStatus} type="type3" />
                </div>
                {/* <div className="payment-info__labels__button">
                  <Button label="요금제 변경" type="type3" action={changePlan} />
                </div> */}
              </div>
              {paymentHistory.length > 0 && <div className="payment-info__content-head">{mainItem.pp_name}</div>}
              <div className="payment-info__content-body">
                <table>
                  <tr className="payment-info__table__tr">
                    <td>
                      <CardIcon />
                    </td>
                    <td className="payment-info__table__td">결제수단</td>
                    <td className="payment-info__table__value">
                      {payCardInfo}
                      {/* <div className="change-payment-type" onClick={toggleModal}>
                        변경
                      </div> */}
                    </td>
                  </tr>
                  <tr className="payment-info__table__tr">
                    <td>
                      <CalendarIcon />
                    </td>
                    <td className="payment-info__table__td">이용기간</td>
                    <td className="payment-info__table__value">
                      {mainItem
                        ? moment(mainItem.rss_start_date).format('YYYY.MM.DD') +
                          ' ~ ' +
                          moment(mainItem.rss_end_date).format('YYYY.MM.DD')
                        : ''}
                    </td>
                  </tr>
                  <tr className="payment-info__table__tr">
                    <td>
                      <Calendar2Icon />
                    </td>
                    <td className="payment-info__table__td">다음 결제예정일</td>
                    <td className="payment-info__table__value">
                      {mainItem ? moment(mainItem.rss_next_date).format('YYYY.MM.DD') : ''}
                    </td>
                  </tr>
                  <tr className="payment-info__table__tr">
                    <td>
                      <CoinIcon />
                    </td>
                    <td className="payment-info__table__td">결제금액</td>
                    <td className="payment-info__table__value">
                      {mainItem ? addComma(mainItem.ppp_discount_price?.toString() ?? '') + '원' : ''}
                    </td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
          <div className="payment-info__bottom">
            <div className="payment-info__content">
              <div className="payment-info__bottom__head">결제 내역</div>
              <table className="payment-details__table">
                <tr className="payment-details__tr fonts">
                  <th className="payment-details__td">No.</th>
                  <th className="payment-details__td">결제일</th>
                  <th className="payment-details__td">이용권</th>
                  <th className="payment-details__td">이용기간</th>
                  <th className="payment-details__td">결제금액</th>
                </tr>
                {paymentHistory.length > 0 ? (
                  paymentHistory.map((pay: StatusType, _i) => (
                    <tr
                      key={`${pay.pp_name}_${pay.rss_start_date}_${pay.rss_end_date}`}
                      className="payment-details__tr fonts">
                      <td>{_i}</td>
                      <td>{moment(pay.rss_created_at).format('YYYY.MM.DD')}</td>
                      <td>{pay.pp_name}</td>
                      <td>
                        {moment(pay.rss_start_date).format('YYYY.MM.DD') +
                          ' ~ ' +
                          moment(pay.rss_end_date).format('YYYY.MM.DD')}
                      </td>
                      <td className="payment-details__td-bold">
                        {addComma(pay.ppp_discount_price?.toString() ?? '')}원
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} style={{ padding: '5%', textAlign: 'center', color: '#ccc' }}>
                      결제 내역이 없습니다
                    </td>
                  </tr>
                )}
              </table>
            </div>
          </div>
        </div>
        {paymentModal && <PaymentModal onClose={toggleModal} />}
      </PopupFrame>
    </>
  );
};
export default PaymentInfo;
